import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWebSocket, usePoll, STATUS, STATUS_FLOW, fmtDateTime, money, missionLabel } from "@/lib/helpers";
import { useSettings } from "@/context/SettingsContext";
import { useVocab } from "@/lib/vocab";
import { useRights } from "@/lib/rights";
import StatusBadge from "@/components/StatusBadge";
import MissionDialog from "@/components/MissionDialog";
import { RideSummary, StepBadge, ReceivedPill, StepTimeline, CorrectStep, DriverPanel, WaitingOffer, supplementsOf } from "@/components/RideParts";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, ArrowRight, Ban, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function Missions() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const voc = useVocab();
  const manager = user.role !== "employee";
  // Lot T5-0 : la secrétaire du transport ne corrige pas d'étape, n'annule, ne réactive ni ne supprime (le crayon
  // reste : la fenêtre s'ouvre selon ses droits). En conciergerie, tout gestionnaire a ce droit : rien ne change.
  const rights = useRights();
  const dispatch = manager && rights.dispatch;
  const transport = voc.business === "transport"; // course : trajet, passagers, véhicule de la flotte, vol (lot T1a)
  const [missions, setMissions] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [fleet, setFleet] = useState([]);
  const [places, setPlaces] = useState([]); // carnet de lieux (lot T1b-1) : chauffeur = les lieux de ses courses
  const [dispatchPhone, setDispatchPhone] = useState(""); // espace chauffeur (lot T4a) : bouton « Dispatch »
  const [toDelete, setToDelete] = useState(null);
  const [toCancel, setToCancel] = useState(null); // mission en attente de confirmation d'annulation
  const [filter, setFilter] = useState("all");
  const [dialog, setDialog] = useState({ open: false, mission: null });
  const cur = settings?.currency || "EUR";

  const load = useCallback(() => {
    api.get("/missions").then((r) => setMissions(r.data));
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    if (transport) api.get("/fleet").then((r) => setFleet(r.data)).catch(() => {});
    if (transport) api.get("/places").then((r) => setPlaces(r.data)).catch(() => {});
    if (transport && !manager) api.get("/ride-contacts/dispatch").then((r) => setDispatchPhone(r.data.phone || "")).catch(() => {});
  }, [transport, manager]);
  useEffect(() => { load(); }, [load]);
  useWebSocket(() => load());
  usePoll(() => load());

  const nameOf = (id, arr, key = "name") => arr.find((x) => x.id === id)?.[key] || "—";
  const filtered = missions.filter((m) => filter === "all" || m.status === filter);

  // « Ferrari 296 GTB (AB-123-CD) » : plaque seulement si elle existe ; rien si la mission n'a pas de véhicule.
  const vehicleOf = (id) => {
    const v = vehicles.find((x) => x.id === id);
    if (!v) return "";
    const nom = [v.brand, v.model].filter(Boolean).join(" ");
    return v.plate ? `${nom} (${v.plate})` : nom;
  };

  // Suppression après confirmation ; une mission liée à une facture ou un devis est refusée par le serveur (message affiché).
  const confirmDelete = async () => {
    const id = toDelete; setToDelete(null);
    try { await api.delete(`/missions/${id}`); toast.success(`${voc.Mission} supprimée`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  // Annulation (après confirmation) et réactivation : refus du serveur affiché (facture, mission terminée, conflit d'horaire).
  const setStatus = async (id, status, ok) => {
    try { await api.patch(`/missions/${id}/status`, { status }); toast.success(ok); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const confirmCancel = () => { const id = toCancel; setToCancel(null); setStatus(id, "cancelled", `${voc.Mission} annulée`); };
  const advance = async (m) => {
    const next = STATUS_FLOW[Math.min(STATUS_FLOW.indexOf(m.status) + 1, 3)];
    await api.patch(`/missions/${m.id}/status`, { status: next }); load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-overline text-accent">Opérations</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1">{voc.Missions}</h1>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="mission-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {manager && <Button className="gap-2" onClick={() => setDialog({ open: true, mission: null })} data-testid="new-mission-button"><Plus className="h-4 w-4" /> Nouvelle</Button>}
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden divide-y divide-border">
        {filtered.length === 0 && <p className="p-6 text-sm text-muted-foreground">{`Aucune ${voc.mission}.`}</p>}
        {filtered.map((m) => (
          <div key={m.id} className="p-4 md:p-5 flex flex-wrap items-center gap-4" data-testid={`mission-row-${m.id}`}>
            {/* Sur téléphone : client, prestation et véhicule sur toute la largeur ; prix et boutons en dessous. */}
            <div className="min-w-0 flex-1 max-md:basis-full">
              <div className="flex items-center gap-3 max-md:flex-wrap max-md:gap-y-1">
                <span className="font-display font-bold">{nameOf(m.client_id, clients)}</span>
                {transport ? <StepBadge m={m} /> : <StatusBadge status={m.status} />}
                {transport && manager && <ReceivedPill m={m} />}
              </div>
              {transport
                ? <RideSummary m={m} fleet={fleet} places={places} employeeName={m.employee_id ? nameOf(m.employee_id, users) : "Non assigné"} />
                : <div className="text-sm text-muted-foreground mt-1">
                {missionLabel(m)}{vehicleOf(m.vehicle_id) && <> · <span data-testid={`mission-vehicle-${m.id}`}>{vehicleOf(m.vehicle_id)}</span></>} · {fmtDateTime(m.start)} → {fmtDateTime(m.end)} · {m.employee_id ? nameOf(m.employee_id, users) : "Non assigné"}
              </div>}
              {/* Espace chauffeur (lot T4a) : suivi pour le dispatch ; boutons, appels et GPS pour le chauffeur. */}
              {transport && manager && <div className="mt-1.5"><StepTimeline m={m} /></div>}
              {transport && manager && m.waiting && <div className="mt-1.5"><WaitingOffer m={m} onDone={load} /></div>}
              {transport && !manager && (
                <DriverPanel m={m} userId={user.id} clientPhone={clients.find((c) => c.id === m.client_id)?.phone}
                  dispatchPhone={dispatchPhone} places={places} onChange={load} />
              )}
            </div>
            {/* Le chauffeur ne voit jamais le prix (transport, lot T4a) : le serveur ne le lui envoie pas. */}
            {/* Lot T6 : frais d'annulation d'une course annulée et facture de la course, sous le prix (transport). */}
            {(manager || !transport) && (transport && (supplementsOf(m) > 0 || m.cancellation_fee > 0 || (m.invoiced || []).length > 0)
              ? <div className="text-right max-md:text-left">
                  <div className={cn("font-medium text-sm", m.status === "cancelled" && "line-through text-muted-foreground font-normal")}>{money(m.price_ttc, cur)}</div>
                  {supplementsOf(m) > 0 && <div className="text-xs text-muted-foreground" data-testid={`mission-supplements-${m.id}`}>dont {money(supplementsOf(m), cur)} de suppléments</div>}
                  {m.status === "cancelled" && m.cancellation_fee > 0 && <div className="text-xs font-medium" data-testid={`mission-cancel-fee-${m.id}`}>Frais d'annulation : {money(m.cancellation_fee, cur)}</div>}
                  {(m.invoiced || []).length > 0 && <div className="text-xs text-muted-foreground" data-testid={`mission-invoiced-${m.id}`}>Facturée : {m.invoiced.join(", ")}</div>}
                </div>
              : <div className="font-medium text-sm">{money(m.price_ttc, cur)}</div>)}
            <div className="flex items-center gap-1">
              {transport && dispatch && <CorrectStep m={m} onDone={load} />}
              {!transport && (manager || m.employee_id === user.id) && m.status !== "completed" && m.status !== "cancelled" && (
                <Button size="sm" variant="outline" className="gap-1" onClick={() => advance(m)} data-testid={`mission-advance-${m.id}`}>
                  {STATUS[STATUS_FLOW[Math.min(STATUS_FLOW.indexOf(m.status) + 1, 3)]].label} <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              )}
              {manager && <>
                {dispatch && m.status === "cancelled" && <Button size="sm" variant="outline" className="gap-1" onClick={() => setStatus(m.id, "todo", `${voc.Mission} réactivée`)} data-testid={`mission-reactivate-${m.id}`}><RotateCcw className="h-3.5 w-3.5" /> Réactiver</Button>}
                <Button size="icon" variant="ghost" onClick={() => setDialog({ open: true, mission: m })} data-testid={`mission-edit-${m.id}`}><Pencil className="h-4 w-4" /></Button>
                {dispatch && ["todo", "enroute", "in_progress"].includes(m.status) && <Button size="icon" variant="ghost" onClick={() => setToCancel(m.id)} title={`Annuler la ${voc.mission}`} data-testid={`mission-cancel-${m.id}`}><Ban className="h-4 w-4" /></Button>}
                {dispatch && <Button size="icon" variant="ghost" onClick={() => setToDelete(m.id)} data-testid={`mission-delete-${m.id}`}><Trash2 className="h-4 w-4" /></Button>}
              </>}
            </div>
          </div>
        ))}
      </div>
      <AlertDialog open={!!toCancel} onOpenChange={(o) => { if (!o) setToCancel(null); }}>
        <AlertDialogContent data-testid="cancel-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{`Annuler cette ${voc.mission} ?`}</AlertDialogTitle>
            <AlertDialogDescription>{`Elle restera dans la liste avec le statut « Annulée », ne comptera plus dans le Journal et libérera le créneau ${voc.ofEmployee}. Vous pourrez la réactiver.`}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-confirm-no">Retour</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancel} data-testid="cancel-confirm-yes">{`Annuler la ${voc.mission}`}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent data-testid="delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{`Supprimer cette ${voc.mission} ?`}</AlertDialogTitle>
            <AlertDialogDescription>Cette action est définitive.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-confirm-no">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="delete-confirm-yes">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <MissionDialog open={dialog.open} onOpenChange={(o) => setDialog({ ...dialog, open: o })} mission={dialog.mission} onSaved={load} />
    </div>
  );
}
